import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { buildApiUrl } from "../config/api";
import "./Cart.css";

const Cart = () => {
  const { cart, removeFromCart, cartTotal } = useCart();
  const [email, setEmail] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCheckout = async () => {
    setLoading(true);
    setError("");

    try {
      // Build items array with only productId, licenseLabel, and quantity (Requirement 5)
      const itemsToSend = cart.map((item) => ({
        productId: item.productId,
        licenseLabel: item.licenseLabel,
        quantity: 1, // backend strictly requires 1 for digital downloads
      }));

      const response = await fetch(buildApiUrl("/payments/create-checkout-session"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: itemsToSend,
          customerEmail: email.trim() || undefined,
          couponCode: couponCode.trim() || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success || !data.url) {
        throw new Error(data.message || "Checkout session could not be created.");
      }

      // Redirect to Stripe-hosted Checkout (Requirement 4)
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || "Failed to start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <main className="cart-page">
      <section className="cart-container">
        <p className="eyebrow">Your Selection</p>
        <h1>Shopping Cart</h1>

        {cart.length === 0 ? (
          <div className="cart-empty-state">
            <p>Your cart is empty.</p>
            <Link to="/shop" className="btn btn-gold">
              Browse Photography
            </Link>
          </div>
        ) : (
          <div className="cart-layout">
            <div className="cart-items-list">
              {cart.map((item) => (
                <article
                  key={`${item.productId}-${item.licenseLabel}`}
                  className="cart-item-card"
                >
                  <div className="cart-item-info">
                    <h2>
                      <Link to={`/product/${item.slug}`}>{item.title}</Link>
                    </h2>
                    <p className="cart-item-license">{item.licenseLabel}</p>
                    <div className="cart-item-meta">
                      <span className="cart-item-quantity">Qty: {item.quantity}</span>
                      <span className="cart-item-price">${Number(item.price).toFixed(2)}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="cart-item-remove-btn"
                    onClick={() => removeFromCart(item.productId, item.licenseLabel)}
                    aria-label={`Remove ${item.title}`}
                  >
                    Remove
                  </button>
                </article>
              ))}
            </div>

            <aside className="cart-summary-sidebar">
              <h2>Secure Checkout</h2>

              <div className="cart-summary-row">
                <span>Subtotal</span>
                <strong>${Number(cartTotal).toFixed(2)}</strong>
              </div>

              <div className="cart-form-group">
                <label htmlFor="checkout-email">
                  Email address
                  <span className="label-note">For download recovery and receipt</span>
                </label>
                <input
                  id="checkout-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className="cart-form-group">
                <label htmlFor="checkout-coupon">Coupon code</label>
                <input
                  id="checkout-coupon"
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Optional"
                />
              </div>

              <p className="cart-summary-note">
                Stripe displays the final database-verified total (including tax and discount)
                on its hosted payment page.
              </p>

              <button
                type="button"
                className="checkout-submit-btn"
                onClick={handleCheckout}
                disabled={loading}
              >
                {loading ? "Opening Stripe..." : "Checkout with Stripe"}
              </button>

              {error && <div className="cart-error-message">{error}</div>}

              <div className="cart-trust-badges">
                <span>Secure SSL Encryption</span>
                <span>Stripe Verified</span>
                <span>Instant Download</span>
              </div>
            </aside>
          </div>
        )}
      </section>
    </main>
  );
};

export default Cart;
